import type { Schemas } from '@argzombie/api-client';
import {
  blockAsParsed,
  containerDefaults,
  convoyDefaults,
  densityTierDefaults,
  discoveryDefaults,
  dropRulesDefaults,
  encounterTypeDefaults,
  encountersDefaults,
  fatigueTierDefaults,
  storeDefaults,
  squadAiDefaults,
  squadRulesDefaults,
  storeProductDefaults,
  survivorTraitDefaults,
  teamRulesDefaults,
  tutorialDropDefaults,
  withDefaults,
  worldEventTypeDefaults,
  worldEventsDefaults,
} from '../teamsDefaults';
import {
  type Bundle,
  type BundleKey,
  MAX_SQUAD_SIZE,
  ENCOUNTER_TYPES,
  WORLD_EVENT_TYPES,
  STORE_PRODUCT_KINDS,
  SQUAD_AI_CHANCES,
  SQUAD_AI_AMOUNTS,
} from './types';
import { num, isChance, type Err, type Ids } from './common';
import { validateCharacters } from './characters';

export type Refs = Record<
  'items' | 'tables' | 'categories' | 'roles' | 'traits',
  Set<string>
>;

/**
 * The Teams on the Map blocks: a port of packages/shared-cs/Runtime/Content/ContentValidator.Teams.cs with the same
 * messages in the same order. Absent blocks, fields, and nested lists get their C# defaults first (see
 * `TEAMS_SECTIONS`); explicit nulls are reported as missing.
 */
export function validateTeams(
  b: Bundle,
  refs: Refs,
  traitsList: (Schemas['SurvivorTraitDef'] | null | undefined)[],
  err: Err,
  ids: Ids,
) {
  /** Checks for one object: `base` prefixes the field path, `where` starts the message (like the C# helpers). */
  const checks = (section: BundleKey, index: number | undefined, base: string, where: string) => {
    const path = (field: string) => (base ? `${base}.${field}` : field);
    return {
      fail: (field: string, message: string) => err(section, index, path(field), message),
      notNegative: (field: string, v: number | null | undefined) => {
        if (num(v) < 0) err(section, index, path(field), `${where} ${field} is negative.`);
      },
      positive: (field: string, v: number | null | undefined) => {
        if (!(num(v) > 0)) err(section, index, path(field), `${where} ${field} must be above 0.`);
      },
      atLeast: (field: string, v: number | null | undefined, min: number) => {
        if (num(v) < min)
          err(section, index, path(field), `${where} ${field} must be at least ${min}.`);
      },
    };
  };

  /** Mirrors `Entries`: the non-null entries (with C# defaults) and their original index, reporting each null. */
  const entries = <T extends object>(
    section: BundleKey,
    base: string,
    list: (T | null | undefined)[],
    emptyError: string,
    defaults: () => T,
  ) => {
    const out: { x: T; j: number }[] = [];
    list.forEach((x, j) => {
      if (x == null) err(section, undefined, `${base}.${j}`, emptyError);
      else out.push({ x: withDefaults(defaults(), x), j });
    });
    return out;
  };

  const str = (v: string | null | undefined) => v ?? '';
  const known = (list: readonly string[], v: string | null | undefined) => list.includes(str(v));

  // Team rules.
  const t = blockAsParsed(teamRulesDefaults, b.teamRules);
  if (t) {
    const c = checks('teamRules', undefined, '', 'Team rules');
    c.atLeast('maxTeams', t.maxTeams, 1);
    c.atLeast('slotsAtStart', t.slotsAtStart, 1);
    if (num(t.slotsAtStart) > num(t.maxTeams))
      c.fail('slotsAtStart', 'Team rules need slotsAtStart <= maxTeams.');
    c.notNegative('thirdSlotAtRoster', t.thirdSlotAtRoster);
    c.atLeast('maxMembersPerTeam', t.maxMembersPerTeam, 1);
    if (num(t.maxMembersPerTeam) > MAX_SQUAD_SIZE)
      c.fail('maxMembersPerTeam', `Team rules need maxMembersPerTeam <= ${MAX_SQUAD_SIZE}.`);
    c.notNegative('memberCarryKg', t.memberCarryKg);
    c.notNegative('energyCap', t.energyCap);
    if (num(t.energyFloor) > 0) c.fail('energyFloor', 'Team rules need energyFloor <= 0.');
    if (num(t.startEnergy) < num(t.energyFloor) || num(t.startEnergy) > num(t.energyCap))
      c.fail('startEnergy', 'Team rules need startEnergy between energyFloor and energyCap.');
    c.positive('travelSpeedKmh', t.travelSpeedKmh);
    c.atLeast('detourFactor', t.detourFactor, 1);
    c.notNegative('travelEnergyPerKm', t.travelEnergyPerKm);
    c.notNegative('raidEnergyCost', t.raidEnergyCost);
    c.notNegative('inPersonEnergyCost', t.inPersonEnergyCost);
    c.notNegative('inPersonBonusLootRolls', t.inPersonBonusLootRolls);
    c.notNegative('regenFieldPerHour', t.regenFieldPerHour);
    c.notNegative('regenHomePerHour', t.regenHomePerHour);
    c.notNegative('regenInfirmaryPerLevelPerHour', t.regenInfirmaryPerLevelPerHour);
    c.positive('rejoinSpeedKmh', t.rejoinSpeedKmh);
    c.notNegative('rejoinMaxMinutes', t.rejoinMaxMinutes);
    c.notNegative('rejoinEnergyCost', t.rejoinEnergyCost);
    c.notNegative('reelInDistanceM', t.reelInDistanceM);
    c.notNegative('tutorialTravelSeconds', t.tutorialTravelSeconds);
    c.atLeast('recruitMaxHealth', t.recruitMaxHealth, 1);
    c.notNegative('recruitDailyHeal', t.recruitDailyHeal);

    // strangerNames is gone (CONTRACT §13.5): the Stranger gets a real name from `names` (validate/names.ts).
    (t.strangerKit ?? []).forEach((st, j) => {
      if (st == null) {
        c.fail(`strangerKit.${j}`, 'Team rules strangerKit has an empty stack.');
        return;
      }
      const id = st.itemId ?? '';
      if (!refs.items.has(id))
        c.fail(`strangerKit.${j}.itemId`, `Team rules strangerKit has unknown item '${id}'.`);
      if (num(st.quantity) <= 0)
        c.fail(
          `strangerKit.${j}.quantity`,
          `Team rules strangerKit '${id}' needs a positive quantity.`,
        );
    });

    if (t.fatigueTiers == null) c.fail('fatigueTiers', "Team rules are missing 'fatigueTiers'.");
    else {
      const tiers = entries(
        'teamRules',
        'fatigueTiers',
        t.fatigueTiers,
        'Team rules have an empty fatigue tier.',
        fatigueTierDefaults,
      );
      ids('teamRules', t.fatigueTiers, 'fatigue tier', 'fatigueTiers');
      tiers.forEach(({ x: f, j }, i) => {
        const fc = checks(
          'teamRules',
          undefined,
          `fatigueTiers.${j}`,
          `Fatigue tier '${str(f.id)}'`,
        );
        const prev = tiers[i - 1]?.x;
        if (prev && num(f.minEnergy) >= num(prev.minEnergy))
          fc.fail(
            'minEnergy',
            `Fatigue tiers need descending minEnergy ('${str(f.id)}' is not below '${str(prev.id)}').`,
          );
        fc.notNegative('maxHealthMult', f.maxHealthMult);
        fc.notNegative('sprintMult', f.sprintMult);
        fc.notNegative('moveNoiseMult', f.moveNoiseMult);
        fc.notNegative('searchTimeMult', f.searchTimeMult);
        fc.notNegative('hordeThresholdMult', f.hordeThresholdMult);
      });
      const last = tiers[tiers.length - 1];
      if (last && num(last.x.minEnergy) > num(t.energyFloor))
        c.fail(
          `fatigueTiers.${last.j}.minEnergy`,
          'The last fatigue tier needs minEnergy <= energyFloor.',
        );
    }
  }

  // Survivor traits.
  traitsList.forEach((raw, i) => {
    if (raw == null) return;
    const tr = withDefaults(survivorTraitDefaults(), raw);
    const c = checks('survivorTraits', i, '', `Survivor trait '${str(tr.id)}'`);
    c.notNegative('travelSpeedMult', tr.travelSpeedMult);
    c.notNegative('carryKg', tr.carryKg);
    c.notNegative('moveNoiseMult', tr.moveNoiseMult);
    c.notNegative('maxHealthBonus', tr.maxHealthBonus);
  });

  // Discovery.
  const d = blockAsParsed(discoveryDefaults, b.discovery);
  if (d) {
    const c = checks('discovery', undefined, '', 'Discovery');
    c.atLeast('destinationCount', d.destinationCount, 1);
    c.positive('radiusM', d.radiusM);
    if (num(d.sparseRadiusM) < num(d.radiusM))
      c.fail('sparseRadiusM', 'Discovery needs sparseRadiusM >= radiusM.');
    c.notNegative('minRealBeforeWiden', d.minRealBeforeWiden);
    c.notNegative('minDistanceM', d.minDistanceM);
    c.notNegative('regenerateMovedM', d.regenerateMovedM);
    c.atLeast('regenerateHours', d.regenerateHours, 1);
    c.atLeast('importRefreshDays', d.importRefreshDays, 1);
    c.notNegative('importMinIntervalSeconds', d.importMinIntervalSeconds);
    c.atLeast('unsafeReportsToHide', d.unsafeReportsToHide, 1);

    if (d.categoryMix == null) c.fail('categoryMix', "Discovery is missing 'categoryMix'.");
    else
      d.categoryMix.forEach((id, j) => {
        if (!refs.categories.has(str(id)))
          c.fail(`categoryMix.${j}`, `Discovery category mix has unknown category '${str(id)}'.`);
      });

    if (d.tiers == null) c.fail('tiers', "Discovery is missing 'tiers'.");
    else {
      const tiers = entries(
        'discovery',
        'tiers',
        d.tiers,
        'Discovery has an empty tier.',
        densityTierDefaults,
      );
      ids('discovery', d.tiers, 'density tier', 'tiers');
      tiers.forEach(({ x: dt, j }, i) => {
        const tc = checks('discovery', undefined, `tiers.${j}`, `Density tier '${str(dt.id)}'`);
        const prev = tiers[i - 1]?.x;
        if (prev && num(dt.minBuildings) >= num(prev.minBuildings))
          tc.fail(
            'minBuildings',
            `Density tiers need descending minBuildings ('${str(dt.id)}' is not below '${str(prev.id)}').`,
          );
        tc.notNegative('minBuildings', dt.minBuildings);
        tc.positive('raidStartRadiusM', dt.raidStartRadiusM);
      });
      const last = tiers[tiers.length - 1];
      if (last && num(last.x.minBuildings) !== 0)
        c.fail(`tiers.${last.j}.minBuildings`, 'The last density tier needs minBuildings 0.');
    }

    const drops = withDefaults(dropRulesDefaults(), d.drops);
    if (drops == null) c.fail('drops', "Discovery is missing 'drops'.");
    else {
      const dc = checks('discovery', undefined, 'drops', 'Discovery drops');
      if (!refs.categories.has(str(drops.categoryId)))
        dc.fail('categoryId', `Discovery drops use unknown category '${str(drops.categoryId)}'.`);
      dc.atLeast('lifetimeDays', drops.lifetimeDays, 1);
      dc.notNegative('maxPerCell', drops.maxPerCell);
      dc.positive('sizeM', drops.sizeM);
      dc.notNegative('excludeDistanceM', drops.excludeDistanceM);
      dc.notNegative('syntheticMinM', drops.syntheticMinM);
      if (num(drops.syntheticMaxM) < num(drops.syntheticMinM))
        dc.fail('syntheticMaxM', 'Discovery drops need syntheticMaxM >= syntheticMinM.');
      if (drops.safeTags == null) dc.fail('safeTags', "Discovery drops are missing 'safeTags'.");
      if (drops.excludeTags == null)
        dc.fail('excludeTags', "Discovery drops are missing 'excludeTags'.");
    }

    const tut = withDefaults(tutorialDropDefaults(), d.tutorial);
    if (tut == null) c.fail('tutorial', "Discovery is missing 'tutorial'.");
    else {
      const tc = checks('discovery', undefined, 'tutorial', 'Discovery tutorial');
      tc.notNegative('minDistanceM', tut.minDistanceM);
      if (num(tut.maxDistanceM) < num(tut.minDistanceM))
        tc.fail('maxDistanceM', 'Discovery tutorial needs maxDistanceM >= minDistanceM.');
      tc.atLeast('expiresHours', tut.expiresHours, 1);
      tc.atLeast('roomCount', tut.roomCount, 1);
      if (num(tut.difficulty) < 1 || num(tut.difficulty) > 5)
        tc.fail('difficulty', 'Discovery tutorial needs difficulty in 1..5.');
      if (!refs.tables.has(str(tut.lootTableId)))
        tc.fail(
          'lootTableId',
          `Discovery tutorial uses unknown loot table '${str(tut.lootTableId)}'.`,
        );
    }
  }

  // Container.
  const ct = blockAsParsed(containerDefaults, b.container);
  if (ct) {
    const c = checks('container', undefined, '', 'Container');
    c.positive('anchorMaxKmh', ct.anchorMaxKmh);
    c.notNegative('anchorJumpM', ct.anchorJumpM);
    c.notNegative('relocateStableMinutes', ct.relocateStableMinutes);
    c.notNegative('relocateStableRadiusM', ct.relocateStableRadiusM);
    c.atLeast('relocateMinPings', ct.relocateMinPings, 1);
    c.positive('relocateMaxKmh', ct.relocateMaxKmh);
    c.notNegative('maxRelocationsPerDay', ct.maxRelocationsPerDay);
  }

  // Convoy.
  const cv = blockAsParsed(convoyDefaults, b.convoy);
  if (cv) {
    const c = checks('convoy', undefined, '', 'Convoy');
    c.positive('startKmh', cv.startKmh);
    c.notNegative('stopKmh', cv.stopKmh);
    if (num(cv.stopKmh) > num(cv.startKmh)) c.fail('stopKmh', 'Convoy needs stopKmh <= startKmh.');
    c.notNegative('stopMinutes', cv.stopMinutes);
    c.atLeast('staleMinutes', cv.staleMinutes, 1);
    c.notNegative('passedCorridorM', cv.passedCorridorM);
    c.notNegative('maxPassedIds', cv.maxPassedIds);
    c.notNegative('maxQueuedOrders', cv.maxQueuedOrders);
    c.atLeast('orderExpiresHours', cv.orderExpiresHours, 1);
  }

  // Encounters.
  const en = blockAsParsed(encountersDefaults, b.encounters);
  if (en) {
    const c = checks('encounters', undefined, '', 'Encounters');
    if (!isChance(num(en.chancePerKm)))
      c.fail('chancePerKm', 'Encounters need chancePerKm in 0..1.');
    c.notNegative('maxPerTrip', en.maxPerTrip);
    c.notNegative('minTripM', en.minTripM);
    const lo = num(en.atFractionMin);
    const hi = num(en.atFractionMax);
    if (!isChance(lo) || !isChance(hi) || lo > hi)
      c.fail('atFractionMin', 'Encounters need 0 <= atFractionMin <= atFractionMax <= 1.');
    // stragglerNames / stragglerSurvivors are gone (CONTRACT §13.5): stragglers get real names from `names`.

    if (en.types == null) c.fail('types', "Encounters are missing 'types'.");
    else {
      const types = entries(
        'encounters',
        'types',
        en.types,
        'Encounters have an empty type.',
        encounterTypeDefaults,
      );
      ids('encounters', en.types, 'encounter type', 'types');
      for (const { x: et, j } of types) {
        const wh = `Encounter '${str(et.id)}'`;
        const tc = checks('encounters', undefined, `types.${j}`, wh);
        if (!known(ENCOUNTER_TYPES, et.id)) tc.fail('id', `${wh} is not a known type.`);
        tc.notNegative('weight', et.weight);
        if (et.lootTableId != null && !refs.tables.has(et.lootTableId))
          tc.fail('lootTableId', `${wh} uses unknown loot table '${et.lootTableId}'.`);
        tc.notNegative('rollsMin', et.rollsMin);
        if (num(et.rollsMax) < num(et.rollsMin))
          tc.fail('rollsMax', `${wh} needs rollsMax >= rollsMin.`);
        if (
          et.choices != null &&
          et.choices.length > 0 &&
          (et.defaultChoice == null || !et.choices.includes(et.defaultChoice))
        )
          tc.fail('defaultChoice', `${wh} needs a defaultChoice from its choices.`);
        tc.notNegative('detourMinutes', et.detourMinutes);
        tc.notNegative('pushEnergy', et.pushEnergy);
        if (!isChance(num(et.timeSavedFraction)))
          tc.fail('timeSavedFraction', `${wh} needs timeSavedFraction in 0..1.`);
        tc.notNegative('fleeHealth', et.fleeHealth);
        tc.notNegative('fleeEnergy', et.fleeEnergy);
        tc.notNegative('roomCount', et.roomCount);
        tc.notNegative('difficultyBonus', et.difficultyBonus);
        tc.notNegative('lootRolls', et.lootRolls);
        if (!isChance(num(et.traitChance)))
          tc.fail('traitChance', `${wh} needs traitChance in 0..1.`);
        (et.offerItemIds ?? []).forEach((id, k) => {
          if (!refs.items.has(str(id)))
            tc.fail(`offerItemIds.${k}`, `${wh} offers unknown item '${str(id)}'.`);
        });
        for (const [id, q] of Object.entries(et.offerQuantity ?? {})) {
          if (!refs.items.has(id))
            tc.fail(`offerQuantity.${id}`, `${wh} has a quantity for unknown item '${id}'.`);
          if (num(q) < 1)
            tc.fail(`offerQuantity.${id}`, `${wh} needs a positive quantity for '${id}'.`);
        }
      }
    }
  }

  // World events.
  const we = blockAsParsed(worldEventsDefaults, b.worldEvents);
  if (we) {
    const c = checks('worldEvents', undefined, '', 'World events');
    c.atLeast('epochHours', we.epochHours, 1);
    if (num(we.geohashPrecision) < 1 || num(we.geohashPrecision) > 12)
      c.fail('geohashPrecision', 'World events need geohashPrecision in 1..12.');
    if (we.types == null) c.fail('types', "World events are missing 'types'.");
    else {
      const types = entries(
        'worldEvents',
        'types',
        we.types,
        'World events have an empty type.',
        worldEventTypeDefaults,
      );
      ids('worldEvents', we.types, 'world event type', 'types');
      for (const { x: et, j } of types) {
        const wh = `World event '${str(et.id)}'`;
        const tc = checks('worldEvents', undefined, `types.${j}`, wh);
        if (!known(WORLD_EVENT_TYPES, et.id)) tc.fail('id', `${wh} is not a known type.`);
        if (!isChance(num(et.chancePerCellEpoch)))
          tc.fail('chancePerCellEpoch', `${wh} needs chancePerCellEpoch in 0..1.`);
        tc.atLeast('everyNthEpoch', et.everyNthEpoch, 1);
        tc.atLeast('durationMinutes', et.durationMinutes, 1);
        tc.notNegative('fullClaims', et.fullClaims);
        if (et.lootTableId != null && !refs.tables.has(et.lootTableId))
          tc.fail('lootTableId', `${wh} uses unknown loot table '${et.lootTableId}'.`);
        if (et.crateLootTableId != null && !refs.tables.has(et.crateLootTableId))
          tc.fail(
            'crateLootTableId',
            `${wh} uses unknown crate loot table '${et.crateLootTableId}'.`,
          );
        tc.notNegative('fullLootRolls', et.fullLootRolls);
        tc.notNegative('lateLootRolls', et.lateLootRolls);
        tc.notNegative('hp', et.hp);
        tc.notNegative('damagePerClear', et.damagePerClear);
        tc.notNegative('difficultyBonus', et.difficultyBonus);
        tc.notNegative('hordeThresholdMult', et.hordeThresholdMult);
        tc.notNegative('crateLootRolls', et.crateLootRolls);
        if (!isChance(num(et.remoteRecruitChance)))
          tc.fail('remoteRecruitChance', `${wh} needs remoteRecruitChance in 0..1.`);
      }
    }
  }

  // Store.
  const st = blockAsParsed(storeDefaults, b.store);
  if (st) {
    const c = checks('store', undefined, '', 'Store');
    c.notNegative('dailyBoughtEnergyCap', st.dailyBoughtEnergyCap);
    c.notNegative('adRewardEnergy', st.adRewardEnergy);
    c.notNegative('adDailyLimit', st.adDailyLimit);
    if (st.products == null) c.fail('products', "Store is missing 'products'.");
    else {
      const products = entries(
        'store',
        'products',
        st.products,
        'Store has an empty product.',
        storeProductDefaults,
      );
      ids('store', st.products, 'store product', 'products');
      for (const { x: p, j } of products) {
        const wh = `Store product '${str(p.id)}'`;
        const pc = checks('store', undefined, `products.${j}`, wh);
        if (!known(STORE_PRODUCT_KINDS, p.kind))
          pc.fail('kind', `${wh} has unknown kind '${str(p.kind)}'.`);
        pc.notNegative('energy', p.energy);
        pc.notNegative('regenMult', p.regenMult);
        if (p.kind === 'consumable' && num(p.energy) <= 0)
          pc.fail('energy', `${wh} needs energy above 0.`);
      }
    }
  }
  // Characters and paid resurrection (ADR 0069): the C# runs it right after Store.
  validateCharacters(b, err);
  // Squad rules (ADR 0027).
  const sq = blockAsParsed(squadRulesDefaults, b.squadRules);
  if (sq) {
    const c = checks('squadRules', undefined, '', 'Squad rules');
    c.notNegative('raidEnergyPerExtraMember', sq.raidEnergyPerExtraMember);
    c.notNegative('inPersonEnergyPerExtraMember', sq.inPersonEnergyPerExtraMember);
    c.notNegative('travelEnergyPerKmPerExtraMember', sq.travelEnergyPerKmPerExtraMember);
    c.notNegative('zombieMultPerExtraMember', sq.zombieMultPerExtraMember);
    c.notNegative('maxLiveZombiesPerExtraMember', sq.maxLiveZombiesPerExtraMember);
    const horde = num(sq.hordeThresholdPerExtraMember);
    if (!(horde >= -0.5 && horde <= 1))
      c.fail(
        'hordeThresholdPerExtraMember',
        'Squad rules need hordeThresholdPerExtraMember in -0.5..1.',
      );
    if (sq.rationItemIds == null)
      c.fail('rationItemIds', "Squad rules are missing 'rationItemIds'.");
    else
      sq.rationItemIds.forEach((id, j) => {
        if (id == null || !refs.items.has(id))
          c.fail(`rationItemIds.${j}`, `Squad rules rationItemIds has unknown item '${str(id)}'.`);
      });
    c.notNegative('rationsPerExtraMember', sq.rationsPerExtraMember);
    c.notNegative('downedBleedOutS', sq.downedBleedOutS);
    c.notNegative('downedBitesToDie', sq.downedBitesToDie);
    c.notNegative('reviveSeconds', sq.reviveSeconds);
    c.notNegative('reviveRangeM', sq.reviveRangeM);
    const revHp = num(sq.reviveHealthFrac);
    if (!(revHp > 0 && revHp <= 1))
      c.fail('reviveHealthFrac', 'Squad rules need reviveHealthFrac above 0 and at most 1.');
    if (num(sq.revivesPerMember) !== 0 && num(sq.revivesPerMember) !== 1)
      c.fail('revivesPerMember', 'Squad rules need revivesPerMember 0 or 1.');
    const largeAt = num(sq.largeRoomsAtSquadSize);
    if (largeAt !== 0 && !(largeAt >= 2 && largeAt <= 4))
      c.fail('largeRoomsAtSquadSize', 'Squad rules need largeRoomsAtSquadSize 0 (never) or 2..4.');
    const minSide = num(sq.largeRoomMinSide);
    if (!(minSide >= 4 && minSide <= 8))
      c.fail('largeRoomMinSide', 'Squad rules need largeRoomMinSide in 4..8.');
    const avgArea = num(sq.largeRoomAverageArea);
    if (!(avgArea >= minSide * minSide && avgArea <= 100))
      c.fail(
        'largeRoomAverageArea',
        'Squad rules need largeRoomAverageArea from largeRoomMinSide squared up to 100.',
      );

    const ai = withDefaults(squadAiDefaults(), sq.ai);
    if (ai == null) c.fail('ai', "Squad rules are missing 'ai'.");
    else {
      const ac = checks('squadRules', undefined, 'ai', 'Squad AI');
      ac.positive('thinkHz', ai.thinkHz);
      ac.notNegative('acquireMinS', ai.acquireMinS);
      if (num(ai.acquireMaxS) < num(ai.acquireMinS))
        ac.fail('acquireMaxS', 'Squad AI needs acquireMaxS >= acquireMinS.');
      ac.notNegative('firstShotWindowS', ai.firstShotWindowS);
      ac.positive('fireIntervalMult', ai.fireIntervalMult);
      ac.notNegative('nearM', ai.nearM);
      if (num(ai.farM) < num(ai.nearM)) ac.fail('farM', 'Squad AI needs farM >= nearM.');
      ac.notNegative('zombieTargetWeight', ai.zombieTargetWeight);
      ac.notNegative('downedTargetWeight', ai.downedTargetWeight);
      ac.notNegative('biteGraceS', ai.biteGraceS);
      ac.notNegative('noiseTaxPerFollower', ai.noiseTaxPerFollower);
      for (const k of SQUAD_AI_CHANCES)
        if (!isChance(num(ai[k]))) ac.fail(k, `Squad AI needs ${k} in 0..1.`);
      for (const k of SQUAD_AI_AMOUNTS) ac.notNegative(k, ai[k]);
      const fan = num(ai.fanBearingDeg);
      if (!(fan >= 90 && fan <= 180))
        ac.fail('fanBearingDeg', 'Squad AI needs fanBearingDeg in 90..180.');
      const noGo = num(ai.noGoHalfAngleDeg);
      if (!(noGo >= 0 && noGo <= 90))
        ac.fail('noGoHalfAngleDeg', 'Squad AI needs noGoHalfAngleDeg in 0..90.');
      const tighten = num(ai.combatTighten);
      if (!(tighten > 0 && tighten <= 1))
        ac.fail('combatTighten', 'Squad AI needs combatTighten above 0 and at most 1.');
      if (num(ai.catchUpFarM) < num(ai.matchNearM))
        ac.fail('catchUpFarM', 'Squad AI needs catchUpFarM >= matchNearM.');
      ac.atLeast('catchUpMult', ai.catchUpMult, 1);
      ac.atLeast('sprintCatchUpMult', ai.sprintCatchUpMult, 1);
      ac.atLeast('crouchCatchUpMult', ai.crouchCatchUpMult, 1);
      if (num(ai.teleportBackMaxM) < num(ai.teleportBackMinM))
        ac.fail('teleportBackMaxM', 'Squad AI needs teleportBackMaxM >= teleportBackMinM.');
      const timeScale = num(ai.orderTimeScale);
      if (!(timeScale > 0 && timeScale <= 1))
        ac.fail('orderTimeScale', 'Squad AI needs orderTimeScale above 0 and at most 1.');
      ac.atLeast('teammateAttackers', ai.teammateAttackers, 1);
      ac.atLeast('playerAttackersCovered', ai.playerAttackersCovered, 1);
      ac.atLeast('playerAttackersAlone', ai.playerAttackersAlone, 1);
      if (num(ai.entryPointMaxM) < num(ai.entryPointMinM))
        ac.fail('entryPointMaxM', 'Squad AI needs entryPointMaxM >= entryPointMinM.');
    }
  }
}
